import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { cn } from '@repo/ui/src/lib/utils';
import type { ClassGroupDetail, EnrollmentMode, PricingType } from '@repo/shared-types';
import type { AddClassMembersInput } from '@repo/validation';
import { useAddClassMembers } from '../../../hooks/useClasses';
import { useSubjects } from '../../../hooks/useSubjects';
import { DatePicker } from '../../../components/pickers/DatePicker';
import { formatVnd } from '../../../lib/formatVnd';
import { todayHouseIso } from '../../../lib/houseToday';
import { buildAddClassMemberRows, type DraftMemberRow } from './confirmRows';

const DAY_LABELS = ['', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const MODES: EnrollmentMode[] = ['SESSION_MONTHLY', 'SESSION_TERM', 'MONTHLY_FLAT'];

function pricingTypeOf(m: EnrollmentMode): PricingType {
  return m === 'MONTHLY_FLAT' ? 'MONTHLY_FLAT' : 'PER_SESSION';
}

interface ConfirmTableProps {
  klass: ClassGroupDetail;
  drafts: DraftMemberRow[];
  onBack: () => void;
  onDone: () => void;
}

export function ConfirmTable({ klass, drafts, onBack, onDone }: ConfirmTableProps) {
  const { t } = useTranslation('scheduling');
  const { data: subjects } = useSubjects();
  const subject = subjects?.find((s) => s.id === klass.subject.id);
  const addMembers = useAddClassMembers(klass.id);

  const [startDate, setStartDate] = useState(todayHouseIso());
  const [mode, setMode] = useState<EnrollmentMode>('SESSION_MONTHLY');
  const [termSize, setTermSize] = useState('');
  const [rows, setRows] = useState<DraftMemberRow[]>(() =>
    drafts.map((d) => ({ ...d, rate: d.existingEnrollment ? String(d.existingEnrollment.rate) : d.rate })));
  const [dateError, setDateError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // Fix wave (review M9) — "Sửa học phí" only OPENS the field for editing;
  // it must not by itself flip `repriceRequested` (which the payload reads
  // as "send existingEnrollmentAction: REPRICE"). That flag is set only
  // once the typed value actually differs from the stored rate, in the
  // Input's own onChange below — otherwise clicking the link and leaving
  // the value untouched ships a no-op REPRICE and writes an empty
  // ENROLLMENT_RATE_CHANGED audit row.
  const [editingRateKeys, setEditingRateKeys] = useState<Set<string>>(() => new Set());

  const allSlotIds = useMemo(() => klass.slots.map((s) => s.id), [klass.slots]);
  const sortedSlots = useMemo(
    () => [...klass.slots].sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime)),
    [klass.slots],
  );
  const batchPricingType = pricingTypeOf(mode);

  const patchRow = (key: string, patch: Partial<DraftMemberRow>) => {
    setRowErrors((prev) => { if (!prev[key]) return prev; const n = { ...prev }; delete n[key]; return n; });
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const toggleSlot = (row: DraftMemberRow, slotId: string) => {
    const current = row.slotIds ?? allSlotIds;
    const next = current.includes(slotId) ? current.filter((id) => id !== slotId) : [...current, slotId];
    patchRow(row.key, { slotIds: next });
  };

  const defaultRatePlaceholder = () => {
    const classDefault = mode === 'MONTHLY_FLAT' ? klass.defaults.monthlyRate : klass.defaults.ratePerSession;
    if (classDefault != null) return String(classDefault);
    const subjectDefault = mode === 'MONTHLY_FLAT' ? subject?.defaultMonthlyRate : subject?.defaultRate;
    return subjectDefault != null ? String(subjectDefault) : undefined;
  };

  const { rows: apiRows, duplicateStudentIds, excludedPausedKeys, includedKeys } = useMemo(
    () => buildAddClassMemberRows(rows, allSlotIds),
    [rows, allSlotIds],
  );
  const submitCount = apiRows.length;
  // Fix wave (review I5) — mirrors `ClassFormSheet`'s own `termOk`: Theo
  // khoá cannot submit with a blank Số buổi. Blocking client-side (instead
  // of letting the server's zod refine 400 with the wire field name
  // "termSize bắt buộc khi billingPeriod = TERM") keeps the batch unsent
  // and marks the field instead of a raw toast.
  const termSizeMissing = mode === 'SESSION_TERM' && termSize.trim().length === 0;

  const submit = () => {
    setDateError(null);
    setFormError(null);
    if (termSizeMissing) return;
    if (duplicateStudentIds.length > 0) {
      setFormError(t('addMembers.confirm.duplicateError'));
      return;
    }
    if (submitCount === 0) return;

    const pricing: AddClassMembersInput['pricing'] = {
      pricingType: batchPricingType,
      ...(batchPricingType === 'PER_SESSION' ? { billingPeriod: mode === 'SESSION_TERM' ? 'TERM' : 'MONTHLY' } : {}),
      ...(mode === 'SESSION_TERM' && termSize.trim() ? { termSize: Number(termSize) } : {}),
    };

    addMembers.mutate(
      { startDate, pricing, rows: apiRows },
      {
        onSuccess: (result) => {
          const added = result.counts.created + result.counts.linked + result.counts.repriced;
          toast.success(t('addMembers.confirm.successToast', { count: added, class: klass.displayName }));
          onDone();
        },
        onError: (err: Error & { code?: string; status?: number; rowIndex?: number; earliestAllowed?: string }) => {
          if (err.code === 'START_DATE_IN_RECONCILED_MONTH' || err.code === 'START_DATE_BEFORE_CLASS') {
            setDateError(err.message);
            return;
          }
          if (err.status === 409) {
            toast.error(err.message || t('addMembers.confirm.retryToast'));
            return;
          }
          const idx = err.rowIndex;
          const key = idx != null ? includedKeys[idx] : undefined;
          if (key) {
            setRowErrors((prev) => ({ ...prev, [key]: err.message }));
            return;
          }
          toast.error(err.message || t('addMembers.confirm.genericError'));
        },
      },
    );
  };

  return (
    // Fix round 1 (item 1): the date/hình-thức controls + table are the
    // ONLY scroll container; the footer (Quay lại / Thêm N học viên) is a
    // `shrink-0` sibling pinned to the bottom of whatever height the parent
    // hands this component (bounded on the 390 Drawer, unbounded — so
    // effectively a no-op — inside the desktop Dialog's own scroll).
    <div className="flex h-full min-h-0 flex-col">
    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pr-0.5">
      <p className="m-0 text-[13px] text-surface-600">
        {t('addMembers.confirm.subtitle', { count: rows.length })}
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-[12.5px] font-medium text-surface-700">{t('addMembers.confirm.startDate')}</span>
          <DatePicker value={startDate} onChange={setStartDate} className="h-8 w-[180px] justify-between font-normal tabular" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12.5px] font-medium text-surface-700">{t('addMembers.confirm.pricingLabel')}</span>
          <div className="inline-flex items-center gap-0.5 rounded-lg border border-surface-200 bg-surface-0 p-0.5">
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'rounded-md px-2.5 py-1.5 text-xs font-medium',
                  mode === m ? 'bg-brand-500 text-white' : 'text-surface-600 hover:bg-surface-50',
                )}
              >
                {t(`addMembers.confirm.pricing.${m}`)}
              </button>
            ))}
          </div>
        </label>
        {mode === 'SESSION_TERM' && (
          <label className="flex flex-col gap-1">
            <span className="text-[12.5px] font-medium text-surface-700">{t('addMembers.confirm.termSize')}</span>
            <Input
              type="number" min="1" value={termSize}
              onChange={(e) => setTermSize(e.target.value)}
              className={cn('h-8 w-[100px] tabular', termSizeMissing && 'border-destructive ring-2 ring-destructive/15')}
            />
          </label>
        )}
      </div>
      {termSizeMissing && <p className="m-0 text-xs text-red-600">{t('addMembers.confirm.termSizeRequired')}</p>}
      {dateError && <p className="m-0 text-xs text-red-600">{dateError}</p>}

      {/* Desktop/tablet: fixed-column grid table (md and up). Below md the
          same rows render as stacked cards (I7) — a fixed-width fee Input
          inside a 4-track grid cannot shrink under 390, and this is the
          last screen before money, so it must never scroll sideways. */}
      <div className="hidden md:block rounded-lg border border-surface-200 overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1fr_1.3fr_2fr] gap-2 items-center px-3 h-8 border-b border-surface-200 bg-surface-50 text-[11px] font-semibold uppercase tracking-wide text-surface-500">
          <span>{t('addMembers.confirm.colStudent')}</span>
          <span>{t(mode === 'MONTHLY_FLAT' ? 'addMembers.confirm.colFeeMonth' : 'addMembers.confirm.colFeeSession')}</span>
          <span>{t('addMembers.confirm.colSlots')}</span>
          <span>{t('addMembers.confirm.colNote')}</span>
        </div>
        <div className="flex flex-col">
          {rows.map((row) => {
            const paused = Boolean(row.existingEnrollment?.paused);
            const mismatch = Boolean(row.existingEnrollment) && row.existingEnrollment!.pricingType !== batchPricingType;
            const editing = editingRateKeys.has(row.key);
            const rateLocked = Boolean(row.existingEnrollment) && !editing;
            const unit = row.existingEnrollment?.pricingType === 'MONTHLY_FLAT' || mode === 'MONTHLY_FLAT'
              ? t('addMembers.unit.month') : t('addMembers.unit.session');
            const partial = row.slotIds !== null && row.slotIds.length < allSlotIds.length;
            const note = paused
              ? t('addMembers.confirm.note.paused')
              : row.existingEnrollment
                ? mismatch
                  ? t('addMembers.confirm.note.pricingLocked', {
                    subject: klass.subject.name, rate: formatVnd(row.existingEnrollment.rate),
                    unit: row.existingEnrollment.pricingType === 'MONTHLY_FLAT' ? t('addMembers.unit.month') : t('addMembers.unit.session'),
                  })
                  : t('addMembers.confirm.note.keepRate', {
                    subject: klass.subject.name, rate: formatVnd(Number(row.rate) || row.existingEnrollment.rate), unit,
                  })
                : row.studentId
                  ? partial
                    ? t('addMembers.confirm.note.existingPartial')
                    : t('addMembers.confirm.note.existingProfile', { subject: klass.subject.name })
                  : t('addMembers.confirm.note.newDefault', { subject: klass.subject.name });

            return (
              <div
                key={row.key}
                className={cn(
                  'grid grid-cols-[1.6fr_1fr_1.3fr_2fr] gap-2 items-center px-3 py-2 min-h-[44px] border-b border-surface-100 last:border-b-0',
                  paused && 'opacity-50',
                )}
              >
                <div className="font-medium text-sm truncate">
                  {row.studentName}
                  {rowErrors[row.key] && <p className="m-0 text-[11px] text-red-600">{rowErrors[row.key]}</p>}
                </div>
                <div>
                  {paused ? (
                    <span className="text-xs text-surface-400">—</span>
                  ) : row.existingEnrollment && rateLocked ? (
                    <span className="inline-flex items-center gap-1.5 text-sm tabular">
                      {formatVnd(row.existingEnrollment.rate)}
                      {!mismatch && (
                        <button
                          type="button"
                          className="text-xs font-medium text-brand-600"
                          onClick={() => setEditingRateKeys((prev) => new Set(prev).add(row.key))}
                        >
                          {t('addMembers.confirm.editRate')}
                        </button>
                      )}
                    </span>
                  ) : (
                    <Input
                      value={row.rate}
                      onChange={(e) => patchRow(row.key, {
                        rate: e.target.value,
                        ...(row.existingEnrollment ? { repriceRequested: e.target.value !== String(row.existingEnrollment.rate) } : {}),
                      })}
                      placeholder={row.existingEnrollment ? undefined : defaultRatePlaceholder()}
                      className="h-8 w-[112px] tabular"
                    />
                  )}
                </div>
                <div className="flex gap-1">
                  {paused ? null : sortedSlots.map((slot) => {
                    const on = row.slotIds === null ? true : row.slotIds.includes(slot.id);
                    return (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => toggleSlot(row, slot.id)}
                        className={cn(
                          'inline-flex h-[22px] min-w-[28px] items-center justify-center rounded-md border px-1 text-[11px] font-semibold',
                          on ? 'border-sky-500/35 bg-sky-100 text-sky-800' : 'border-dashed border-surface-300 text-surface-400',
                        )}
                      >
                        {DAY_LABELS[slot.dayOfWeek]}
                      </button>
                    );
                  })}
                </div>
                <p className="m-0 text-[12.5px] leading-snug text-surface-600">{note}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Phone (below md): one card per row — name, fee, khung giờ chips,
          note stacked, never a horizontal scroll (I7). */}
      <div className="flex md:hidden flex-col gap-2">
        {rows.map((row) => {
          const paused = Boolean(row.existingEnrollment?.paused);
          const mismatch = Boolean(row.existingEnrollment) && row.existingEnrollment!.pricingType !== batchPricingType;
          const editing = editingRateKeys.has(row.key);
          const rateLocked = Boolean(row.existingEnrollment) && !editing;
          const unit = row.existingEnrollment?.pricingType === 'MONTHLY_FLAT' || mode === 'MONTHLY_FLAT'
            ? t('addMembers.unit.month') : t('addMembers.unit.session');
          const partial = row.slotIds !== null && row.slotIds.length < allSlotIds.length;
          const note = paused
            ? t('addMembers.confirm.note.paused')
            : row.existingEnrollment
              ? mismatch
                ? t('addMembers.confirm.note.pricingLocked', {
                  subject: klass.subject.name, rate: formatVnd(row.existingEnrollment.rate),
                  unit: row.existingEnrollment.pricingType === 'MONTHLY_FLAT' ? t('addMembers.unit.month') : t('addMembers.unit.session'),
                })
                : t('addMembers.confirm.note.keepRate', {
                  subject: klass.subject.name, rate: formatVnd(Number(row.rate) || row.existingEnrollment.rate), unit,
                })
              : row.studentId
                ? partial
                  ? t('addMembers.confirm.note.existingPartial')
                  : t('addMembers.confirm.note.existingProfile', { subject: klass.subject.name })
                : t('addMembers.confirm.note.newDefault', { subject: klass.subject.name });

          return (
            <div
              key={row.key}
              className={cn(
                'flex flex-col gap-1.5 rounded-lg border border-surface-200 p-3',
                paused && 'opacity-50',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{row.studentName}</span>
                {paused ? (
                  <span className="text-xs text-surface-400">—</span>
                ) : row.existingEnrollment && rateLocked ? (
                  <span className="inline-flex items-center gap-1.5 text-sm tabular shrink-0">
                    {formatVnd(row.existingEnrollment.rate)}
                    {!mismatch && (
                      <button
                        type="button"
                        className="text-xs font-medium text-brand-600"
                        onClick={() => setEditingRateKeys((prev) => new Set(prev).add(row.key))}
                      >
                        {t('addMembers.confirm.editRate')}
                      </button>
                    )}
                  </span>
                ) : (
                  <Input
                    value={row.rate}
                    onChange={(e) => patchRow(row.key, {
                      rate: e.target.value,
                      ...(row.existingEnrollment ? { repriceRequested: e.target.value !== String(row.existingEnrollment.rate) } : {}),
                    })}
                    placeholder={row.existingEnrollment ? undefined : defaultRatePlaceholder()}
                    className="h-8 w-[112px] shrink-0 tabular"
                  />
                )}
              </div>
              {rowErrors[row.key] && <p className="m-0 text-[11px] text-red-600">{rowErrors[row.key]}</p>}
              {!paused && (
                <div className="flex flex-wrap gap-1">
                  {sortedSlots.map((slot) => {
                    const on = row.slotIds === null ? true : row.slotIds.includes(slot.id);
                    return (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => toggleSlot(row, slot.id)}
                        // Re-review fix (new defect 4, §6.1): 44px tap
                        // target on this phone-only card (the desktop
                        // grid's identical chip above keeps its
                        // mouse-sized 22px) — an invisible before:
                        // pseudo-element extends the HIT area without
                        // growing the visible pill, since seven of these
                        // side by side at a full 44px would crowd the last
                        // screen before money.
                        className={cn(
                          'relative inline-flex h-[22px] min-w-[28px] items-center justify-center rounded-md border px-1 text-[11px] font-semibold',
                          'before:absolute before:-inset-[11px] before:content-[""]',
                          on ? 'border-sky-500/35 bg-sky-100 text-sky-800' : 'border-dashed border-surface-300 text-surface-400',
                        )}
                      >
                        {DAY_LABELS[slot.dayOfWeek]}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="m-0 text-[12.5px] leading-snug text-surface-600">{note}</p>
            </div>
          );
        })}
      </div>

      {formError && <p className="m-0 text-sm text-red-600">{formError}</p>}
      {excludedPausedKeys.length > 0 && (
        <p className="m-0 text-xs text-surface-500">
          {t('addMembers.confirm.excludedNote', { count: excludedPausedKeys.length })}
        </p>
      )}
    </div>

      <div className="shrink-0 flex flex-col gap-1.5 border-t border-surface-200 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="text-[12.5px] text-surface-500">{t('addMembers.confirm.repriceHint')}</span>
          {mode === 'SESSION_TERM' && <span className="text-[12.5px] text-surface-500">{t('addMembers.confirm.termFooterNote')}</span>}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={onBack} disabled={addMembers.isPending}>{t('addMembers.back')}</Button>
          <Button type="button" onClick={submit} disabled={addMembers.isPending || submitCount === 0 || termSizeMissing}>
            {addMembers.isPending ? t('addMembers.confirm.submitting') : t('addMembers.confirm.submit', { count: submitCount })}
          </Button>
        </div>
      </div>
    </div>
  );
}
